import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useMemo } from 'react';
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native';

import { Text } from '@/components/ui';
import { useFitsReports } from '@/hooks/use-fits-reports';
import { usePermissions } from '@/hooks/use-permissions';

/**
 * Quick Links (#279) — the icon grid at the bottom of the dashboard.
 *
 * Duplicates destinations the sidebar already has, deliberately: the sidebar is for
 * navigating when you know where you are going, this is for the handful of places people
 * end up most, as large tap targets. It matters most on a tablet in a truck, where the
 * drawer is a slide-over rather than a permanent rail.
 *
 * Every tile is permission-gated, so nobody is offered a destination that will 403 or 404
 * on arrival.
 */

interface LinkTile {
	label: string;
	icon: keyof typeof Ionicons.glyphMap;
	href: string;
	show: boolean;
}

export const QuickLinks: React.FC<{ style?: ViewStyle }> = ({ style }) => {
	const permissions = usePermissions();
	const fitsReports = useFitsReports();

	const tiles = useMemo<LinkTile[]>(() => (([
		{ label: 'Work Orders', icon: 'construct-outline', href: '/work-orders', show: true },
		{ label: 'Quotes', icon: 'document-text-outline', href: '/quotes', show: true },
		// Paused work — see the note in v2-nav-groups.ts. Maintenance staff only.
		{
			label: 'Pending',
			icon: 'hourglass-outline',
			href: '/pending',
			show: permissions.isMaintenance || permissions.isContractor,
		},
		{
			label: 'Materials',
			icon: 'cube-outline',
			href: '/materials',
			show: permissions.canViewMaterials,
		},
		{
			label: 'Reports',
			icon: 'stats-chart-outline',
			href: '/reports',
			// Same device rule as the drawer row: no Reports on a phone.
			show: permissions.canViewReports && fitsReports,
		},
		{ label: 'Documents', icon: 'folder-open-outline', href: '/documents', show: true },
		// The one association list — with accounting figures for flagged viewers. The old
		// "Accounting" tile pointed at the retired `/accounting` picker.
		{ label: 'Associations', icon: 'business-outline', href: '/associations', show: true },
	] as LinkTile[]).filter((t) => t.show)), [permissions, fitsReports]);

	return (
		<View style={[styles.card, style]} testID="v2-quick-links">
			<View style={styles.header}>
				<Ionicons name="link-outline" size={18} color="#546e7a" />
				<Text style={styles.title}>Quick Links</Text>
			</View>
			<View style={styles.grid}>
				{tiles.map((t) => (
					<Pressable
						key={t.href}
						onPress={() => router.push(t.href as never)}
						style={styles.tile}
						testID={`v2-quicklink-${t.href}`}
					>
						<Ionicons name={t.icon} size={24} color="#546e7a" />
						<Text numberOfLines={1} style={styles.label}>{t.label}</Text>
					</Pressable>
				))}
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	card: {
		backgroundColor: '#fff',
		borderRadius: 12,
		borderWidth: 1,
		borderColor: '#e8eaed',
		padding: 18,
	},
	header: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 14 },
	title: { flex: 1, fontSize: 16, fontWeight: '700', color: '#263238' },
	grid: { flexDirection: 'row', gap: 10, flexWrap: 'wrap' },
	tile: {
		alignItems: 'center',
		justifyContent: 'center',
		gap: 8,
		/**
		 * Grow to fill the row, wrap below a legible width.
		 *
		 * Fixed 92px tiles left a ragged band of dead space on the right of a wide card.
		 * `flexBasis` is the width each tile WANTS — the wrap threshold — and `flexGrow`
		 * shares whatever is left over equally, so a row of seven fills edge to edge and a
		 * narrow window wraps instead of shrinking them into illegibility.
		 *
		 * `minWidth: 0` because RN-Web flex children default to `min-width: auto`, which
		 * would let a long label ("Associations") refuse to shrink and force the row wider
		 * than the card.
		 */
		flexGrow: 1,
		flexBasis: 92,
		minWidth: 0,
		height: 82,
		borderRadius: 10,
		borderWidth: 1,
		borderColor: '#e8eaed',
		backgroundColor: '#fbfcfd',
	},
	label: { fontSize: 12, color: '#455a64', fontWeight: '500' },
});
